import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { CalendarCheck, Loader2, Video, MapPin, Calendar, Download } from "lucide-react";
import AcceptTerms from "../components/AcceptTerms";

const initialForm = {
  type: "free_virtual",
  name: "", email: "", phone: "", organization: "",
  preferred_date: "", preferred_time: "10:00",
  topic: "", notes: "",
};

export default function BookConsultation() {
  const [form, setForm] = useState(initialForm);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null); // booking obj when free; null otherwise

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data: booking } = await api.post("/bookings", { ...form, origin_url: window.location.origin });
      if (booking.type === "paid_in_person") {
        const { data } = await api.post("/checkout/booking", {
          booking_id: booking.id, origin_url: window.location.origin,
        });
        window.location.href = data.url;
      } else {
        setDone(booking);
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit booking.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    const icsUrl = `${apiBase}/api/bookings/${done.id}/calendar.ics`;
    const openGoogle = async () => {
      try {
        const { data } = await api.get(`/bookings/${done.id}/google-calendar`);
        window.open(data.url, "_blank", "noopener,noreferrer");
      } catch (_e) {}
    };
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-32 text-center" data-testid="booking-success">
        <CalendarCheck className="w-12 h-12 text-[var(--red)] mx-auto mb-6" />
        <h1 className="font-display text-4xl font-semibold tracking-tight mb-4">Consultation request received</h1>
        <p className="text-[var(--text-2)] mb-2">We've emailed a confirmation to <span className="text-white">{done.email}</span>.</p>
        <p className="text-[var(--text-2)]">We'll confirm <span className="text-white">{done.preferred_date} at {done.preferred_time}</span> shortly.</p>

        <div className="mt-10 flex flex-wrap gap-3 justify-center" data-testid="booking-calendar-actions">
          <button onClick={openGoogle} className="btn-red" data-testid="add-to-google-calendar">
            <Calendar className="w-4 h-4" /> Add to Google Calendar
          </button>
          <a href={icsUrl} className="btn-ghost" data-testid="download-ics" download>
            <Download className="w-4 h-4" /> Download .ics (Apple / Outlook)
          </a>
        </div>
        <p className="text-xs text-[var(--text-3)] mt-4">The .ics file is also attached to your confirmation email.</p>

        <a href="/" className="btn-ghost mt-8 inline-flex">Back to Home</a>
      </div>
    );
  }

  const buttonLabel = renderSubmitLabel(submitting, form.type);

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-16 pb-24" data-testid="book-page">
      <div className="py-12">
        <div className="overline text-[var(--red)] mb-4">Book a consultation</div>
        <h1 className="font-display font-semibold text-4xl lg:text-5xl tracking-tight max-w-2xl leading-[1.05]">
          Start with a conversation.
        </h1>
        <p className="text-[var(--text-2)] mt-4 max-w-xl">Choose a 30-min free virtual call, or schedule a paid in-person consultation ($250 / 60 min).</p>
      </div>

      <form onSubmit={submit} className="card-dark p-8 space-y-6" data-testid="booking-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <button type="button" onClick={() => set("type", "free_virtual")}
            className={`p-5 border text-left transition ${form.type === "free_virtual" ? "border-[var(--red)] bg-[var(--surface-hover)]" : "border-[var(--border)] hover:border-[var(--silver)]"}`}
            data-testid="book-type-free">
            <Video className="w-5 h-5 text-[var(--red)] mb-3" />
            <div className="font-display font-semibold">Free Virtual Consultation</div>
            <div className="text-xs text-[var(--text-2)] mt-1">30 min · Video call · Free</div>
          </button>
          <button type="button" onClick={() => set("type", "paid_in_person")}
            className={`p-5 border text-left transition ${form.type === "paid_in_person" ? "border-[var(--red)] bg-[var(--surface-hover)]" : "border-[var(--border)] hover:border-[var(--silver)]"}`}
            data-testid="book-type-paid">
            <MapPin className="w-5 h-5 text-[var(--red)] mb-3" />
            <div className="font-display font-semibold">Paid In-Person Consultation</div>
            <div className="text-xs text-[var(--text-2)] mt-1">60 min · Las Vegas, NV · $250</div>
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="overline mb-2 block">Full name</label>
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} className="input-dark" data-testid="book-name" />
          </div>
          <div>
            <label className="overline mb-2 block">Email</label>
            <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input-dark" data-testid="book-email" />
          </div>
          <div>
            <label className="overline mb-2 block">Phone</label>
            <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="input-dark" data-testid="book-phone" />
          </div>
          <div>
            <label className="overline mb-2 block">Organization</label>
            <input value={form.organization} onChange={(e) => set("organization", e.target.value)} className="input-dark" data-testid="book-org" />
          </div>
          <div>
            <label className="overline mb-2 block">Preferred date</label>
            <input required type="date" value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} className="input-dark" data-testid="book-date" />
          </div>
          <div>
            <label className="overline mb-2 block">Preferred time</label>
            <input required type="time" value={form.preferred_time} onChange={(e) => set("preferred_time", e.target.value)} className="input-dark" data-testid="book-time" />
          </div>
        </div>

        <div>
          <label className="overline mb-2 block">Topic / focus area</label>
          <input required value={form.topic} onChange={(e) => set("topic", e.target.value)} className="input-dark" placeholder="e.g., Logic model for CDC grant" data-testid="book-topic" />
        </div>
        <div>
          <label className="overline mb-2 block">Notes (optional)</label>
          <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="input-dark" rows={4} data-testid="book-notes" />
        </div>

        <p className="text-xs text-[var(--text-3)]">After submitting, you'll receive a confirmation email and an <span className="text-white">.ics</span>-compatible calendar invite (Google / iCloud / Outlook).</p>
        <AcceptTerms checked={acceptedTerms} onChange={setAcceptedTerms} testId="booking-accept-terms" />

        {error && <div className="text-sm text-[var(--red)]" data-testid="book-error">{error}</div>}
        <button type="submit" disabled={submitting} className="btn-red" data-testid="book-submit">
          {buttonLabel}
        </button>
      </form>
    </div>
  );
}

function renderSubmitLabel(submitting, type) {
  if (submitting) {
    return (
      <>
        <Loader2 className="w-4 h-4 animate-spin" />{" "}
        {type === "paid_in_person" ? "Redirecting to payment…" : "Submitting…"}
      </>
    );
  }
  return type === "paid_in_person" ? "Continue to payment ($250)" : "Request consultation";
}
